import { lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import AdminProvider from '../context/AdminProvider'
import DivisionProvider from '../context/DivisionProvider'
import SuperAdminProvider from '../context/SuperAdminProvider'
import UserProvider from '../context/UserProvider'
import { StoreState } from '../redux/reducers'
import { AuthAdmin } from '../redux/reducers/authAdmin.reducer'
import SpinWinWheel from '../pages/user/SpinWinWheel'

const AboutUs = lazy(() => import('../pages/AboutUs'))
const Blog = lazy(() => import('../pages/Blog'))
const BlogPost = lazy(() => import('../pages/BlogPost'))
const ContactUs = lazy(() => import('../pages/ContactUs'))
const NotFound = lazy(() => import('../pages/NotFound'))
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'))
const ProductView = lazy(() => import('../pages/ProductView'))
const ProductViewAppointment = lazy(() => import('../pages/ProductViewAppointment'))
const ProductViewEvent = lazy(() => import('../pages/ProductViewEvent'))
const ProductViewV2 = lazy(() => import('../pages/ProductViewV2'))
const SharingCart = lazy(() => import('../pages/SharingCart'))
const TermsAndConditions = lazy(() => import('../pages/TermsAndConditions'))
const AppointmentBookings = lazy(() => import('../pages/admin/AppointmentBookings'))
const AdminAppointments = lazy(() => import('../pages/admin/Appointments'))
const AdminCategories = lazy(() => import('../pages/admin/Categories'))
const AdminChefDashboard = lazy(() => import('../pages/admin/ChefDashboard'))
const AdminEvents = lazy(() => import('../pages/admin/Events'))
const AdminFeedbacks = lazy(() => import('../pages/admin/Feedbacks'))
const AdminFeaturedProducts = lazy(() => import('../pages/admin/FeaturedProducts'))
const AdminFoods = lazy(() => import('../pages/admin/Foods'))
const AdminHome = lazy(() => import('../pages/admin/Home'))
const AdminOrders = lazy(() => import('../pages/admin/Orders'))
const AdminReports = lazy(() => import('../pages/admin/Reports'))
const AdminDeliveryRequest = lazy(() => import('../pages/admin/deliveryRequest'))
const AdminReservations = lazy(() => import('../pages/admin/Reservations'))
const AdminSettings = lazy(() => import('../pages/admin/Settings'))
const AdminVolunteerReservations = lazy(() => import('../pages/admin/VolunteerReservations'))
const AdminPointsQr = lazy(() => import('../pages/admin/PointsQr'))
const AdminLoyaltyRedeem = lazy(() => import('../pages/admin/LoyaltyRedeem'))
const AdminVouchers = lazy(() => import('../pages/admin/Vouchers'))
const DivisionShop = lazy(() => import('../pages/division/DivisionShop'))
const Home = lazy(() => import('../pages/division/Home'))
const SuperAdminBlogs = lazy(() => import('../pages/super/Blogs'))
const SuperAdminDivisions = lazy(() => import('../pages/super/Divisions'))
const SuperAdminHome = lazy(() => import('../pages/super/Home'))
const SuperAdminReviews = lazy(() => import('../pages/super/Reviews'))
const SuperAdminListing = lazy(() => import('../pages/super/Listing'))
const SuperAdminAdvertisements = lazy(() => import('../pages/super/Advertisements'))
const SuperAdminShops = lazy(() => import('../pages/super/Shops'))
const SuperAdminUsers = lazy(() => import('../pages/super/Users'))
const UserAppointments = lazy(() => import('../pages/user/Appointments'))
const UserBookings = lazy(() => import('../pages/user/Bookings'))
const UserCheckout = lazy(() => import('../pages/user/Checkout'))
const UserCheckoutAppointment = lazy(() => import('../pages/user/CheckoutAppointment'))
const UserCheckoutEvent = lazy(() => import('../pages/user/CheckoutEvent'))
const UserEventBooked = lazy(() => import('../pages/user/EventBooked'))
const UserHome = lazy(() => import('../pages/user/Home'))
const UserMyProfile = lazy(() => import('../pages/user/MyProfile'))
const UserMyListing = lazy(() => import('../pages/user/MyListing'))
const UserPointsScan = lazy(() => import('../pages/user/PointsScan'))
const UserOrderPlaced = lazy(() => import('../pages/user/OrderPlaced'))
const UserOrders = lazy(() => import('../pages/user/Orders'))
const UserReservationBooked = lazy(() => import('../pages/user/ReservationBooked'))
const UserShop = lazy(() => import('../pages/user/Shop'))
const ListingPost = lazy(() => import('../pages/user/ListingPost'))

const PageLoader = () => (
    <div className='d-flex justify-content-center align-items-center min-vh-100'>
        <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
        </div>
    </div>
)

const Router = () => {
    const authAdminRole = useSelector<StoreState, AuthAdmin | null>(state => state.authAdmin)?.role
    return <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
            <Routes>
                <Route path='/' element={<UserProvider />}>
                    <Route index element={<UserHome />} />
                    <Route path='checkout' element={<UserCheckout />} />
                    <Route path='checkout-event' element={<UserCheckoutEvent />} />
                    <Route path='checkout-appointment' element={<UserCheckoutAppointment />} />
                    <Route path='points/scan/:shopId' element={<UserPointsScan />} />
                    <Route path='order-placed/:orderNumber' element={<UserOrderPlaced />} />
                    <Route path='event-booked/:bookingNumber' element={<UserEventBooked />} />
                    <Route path='appointment-booked/:reservationNumber' element={<UserReservationBooked />} />
                    <Route path='spin-wheel' element={<SpinWinWheel />} />
                    <Route path='shop'>
                        <Route path=':shopId' element={<UserShop />} />
                    </Route>
                    <Route path='orders' element={<UserOrders />} />
                    <Route path='bookings' element={<UserBookings />} />
                    <Route path='appointments' element={<UserAppointments />} />
                    <Route path='profile' element={<UserMyProfile />} />
                    <Route path='listings' element={<UserMyListing />} />
                    <Route path='about-us' element={<AboutUs />} />
                    <Route path='contact-us' element={<ContactUs />} />
                    <Route path='privacy-policy' element={<PrivacyPolicy />} />
                    <Route path='terms-and-conditions' element={<TermsAndConditions />} />
                    <Route path='product-view/:shopId' element={<ProductView />} />
                    <Route path='product/:shopId/:productId' element={<ProductViewV2 />} />
                    <Route path='event/:shopId/:eventId' element={<ProductViewEvent />} />
                    <Route path='cart/:shopId/:cartId' element={<SharingCart />} />
                    <Route path='appointment/:shopId/:appointmentId' element={<ProductViewAppointment />} />
                    <Route path='blog' element={<Blog />} />
                    <Route path='blog-post'>
                        <Route path=':blogId' element={<BlogPost />} />
                    </Route>
                    <Route path='listing-post'>
                        <Route path=':listingId' element={<ListingPost />} />
                    </Route>
                </Route>

                <Route path='/admin' element={<AdminProvider />}>
                    <Route index element={authAdminRole === 'chef'
                        ? <AdminChefDashboard />
                        : <AdminHome />} />
                    <Route path='orders' element={<AdminOrders />} />
                    <Route path='reservations' element={<AdminReservations />} />
                    <Route path='appointment-bookings' element={<AppointmentBookings />} />
                    <Route path='volunteering' element={<AdminVolunteerReservations />} />
                    <Route path='products' element={<AdminFoods />} />
                    <Route path='featured' element={<AdminFeaturedProducts />} />
                    <Route path='appointments' element={<AdminAppointments />} />
                    <Route path='categories' element={<AdminCategories />} />
                    <Route path='vouchers' element={<AdminVouchers />} />
                    <Route path='points-qr' element={<AdminPointsQr />} />
                    <Route path='loyalty-redeem' element={<AdminLoyaltyRedeem />} />
                    <Route path='feedbacks' element={<AdminFeedbacks />} />
                    <Route path='reports' element={<AdminReports />} />
                    <Route path='delivery-request' element={<AdminDeliveryRequest />} />
                    <Route path='settings' element={<AdminSettings />} />
                    <Route path='events' element={<AdminEvents />} />
                </Route>

                <Route path='/super' element={<SuperAdminProvider />}>
                    <Route index element={<SuperAdminHome />} />
                    <Route path='divisions' element={<SuperAdminDivisions />} />
                    <Route path='shops' element={<SuperAdminShops />} />
                    <Route path='users' element={<SuperAdminUsers />} />
                    <Route path='blogs' element={<SuperAdminBlogs />} />
                    <Route path='messages' element={<SuperAdminReviews />} />
                    <Route path='listing' element={<SuperAdminListing />} />
                    <Route path='advertisements' element={<SuperAdminAdvertisements />} />
                </Route>
                <Route path='/division' element={<DivisionProvider />}>
                    <Route index element={<Home />} />
                    <Route path='divisionShops' element={<DivisionShop />} />
                </Route>
                <Route path='*' element={<NotFound />} />
            </Routes>
        </Suspense>
    </BrowserRouter>
}

export default Router
